import React, { useState, useEffect } from 'react';
import swal from 'sweetalert';
import Loader from '../components/utils/Loader';
import Header from './Header';
import { getMerchantData, SignUP, updateMerchants } from '../components/actions/auth';
import { EyeIcon, EyeSlashIcon, UserPlusIcon, UserIcon, PencilIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import DashboardLayout from '../components/layouts/DashboardLayout';

// Reusable Row Component
const InfoRow = ({ label, value, big, color = "text-gray-900" }) => (
  <div className="flex justify-between items-center py-3 border-b border-gray-100 last:border-0">
    <span className="font-medium text-gray-600">{label}</span>
    <span className={`font-semibold ${big ? "text-2xl text-blue-600" : color}`}>
      {value || "-"}
    </span>
  </div>
);

function MerchantList() {
  const [merchants, setMerchants] = useState([]);
  const [filteredMerchants, setFilteredMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // Modal States
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [editingMerchant, setEditingMerchant] = useState(null);

  // Password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form Data
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contactNumber: "",
    newPassword: "",
    confirmPassword: "",
    industry: "",
    companyName: "",
    projectDetails: "",
    tAndC: false,
    purpose: "",
    status: true,
  });

  const [errors, setErrors] = useState({});
  const code = sessionStorage.getItem("code");
  useEffect(() => {
    fetchMerchants();
  }, [code]);

  const fetchMerchants = async () => {
    setLoading(true);
    try {
      const res = await getMerchantData("MER35806788");
      if (res.message === "success" && Array.isArray(res.data?.merchants)) {
        setMerchants(res.data.merchants);
        setFilteredMerchants(res.data.merchants);
      }
    } catch (err) {
      swal("Error", "Failed to load merchants", "error");
    }
    setLoading(false);
  };

  // SEARCH + FILTER
  useEffect(() => {
    let filtered = [...merchants];

    if (searchTerm.trim() !== "") {
      filtered = filtered.filter((m) => {
        const name = m.Name?.toLowerCase() || "";
        const email = m.Email?.toLowerCase() || "";
        const mobile = m.Mobile || "";
        const codeVal = m.Code?.toLowerCase() || "";
        const industry = m.Industry?.toLowerCase() || "";
        return (
          name.includes(searchTerm.toLowerCase()) ||
          email.includes(searchTerm.toLowerCase()) ||
          mobile.includes(searchTerm) ||
          codeVal.includes(searchTerm.toLowerCase()) ||
          industry.includes(searchTerm.toLowerCase())
        );
      });
    }

    if (statusFilter === "Active") {
      filtered = filtered.filter((m) => m.Status === true || m.Status === 1);
    }
    if (statusFilter === "Inactive") {
      filtered = filtered.filter((m) => m.Status === false || m.Status === 0);
    }

    setFilteredMerchants(filtered);
  }, [searchTerm, statusFilter, merchants]);

  // STATS
  const totalMerchants = merchants.length;
  const totalEarnedPoints = merchants.reduce((sum, m) => sum + (m.earnedpoint || 0), 0);
  const totalRedeemedValue = merchants.reduce((sum, m) => sum + (m.RedeemValue || 0), 0);
  const activeMerchants = merchants.filter((m) => m.Status === true || m.Status === 1).length;

  // MODAL HANDLERS
  const openDetail = (merchant) => {
    setSelectedMerchant(merchant);
    setShowDetailModal(true);
  };

  const openEditModal = (merchant) => {
    console.log(merchant)
    setEditingMerchant(merchant);
    setFormData({
      name: merchant.Name || "",
      email: merchant.Email || "",
      contactNumber: merchant.Mobile || "",
      companyName: merchant.Company || "",
      industry: merchant.Industry || "",
      projectDetails: merchant.Description || "",
      purpose: merchant.Purpose || "",
      newPassword: "",
      confirmPassword: "",
      tAndC: true,
      status: merchant.Status === true || merchant.Status === 1,
    });
    setErrors({});
    setShowEditModal(true);
  };

  const closeAllModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDetailModal(false);
    setSelectedMerchant(null);
    setEditingMerchant(null);
    setFormData({
      name: "", email: "", contactNumber: "", newPassword: "", confirmPassword: "",
      industry: "", companyName: "", projectDetails: "", tAndC: false, purpose: "", status: true,
    });
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const err = {};
    if (!formData.name.trim()) err.name = "Name is required";
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
      err.email = "Valid email required";

    if (showAddModal) {
      if (!formData.newPassword || formData.newPassword.length < 6)
        err.newPassword = "Password must be at least 6 characters";
      if (formData.newPassword !== formData.confirmPassword)
        err.confirmPassword = "Passwords do not match";
    }

    if (!formData.contactNumber || !/^\d{10,15}$/.test(formData.contactNumber))
      err.contactNumber = "Valid mobile number required";
    if (!formData.industry) err.industry = "Select industry";
    if (!formData.companyName.trim()) err.companyName = "Company name is required";
    if (!formData.projectDetails || formData.projectDetails.length < 20)
      err.projectDetails = "Minimum 20 characters required";
    if (!formData.purpose) err.purpose = "Select purpose";
    if (!formData.tAndC) err.tAndC = "You must agree to terms";

    return err;
  };

  const handleAddMerchant = async () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length) return setErrors(newErrors);

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        mobile: formData.contactNumber,
        password: formData.newPassword,
        company: formData.companyName,
        portalName: formData.companyName.replace(/\s+/g, "").toLowerCase().substring(0, 10),
        industry: formData.industry,
        purpose: formData.purpose,
        description: formData.projectDetails,
        code: "abc",
        country: "India",
        timeZone: "Asia/Kolkata",
        city: "Mumbai",
        status: formData.status ? 1 : 0,
        isRedemption: 0,
        merchantClientId: "temp",
        merchantClientSecret: "temp",
        remarks: "Added by admin",
        adminCode: "MER35806788",
        isAdmin: false,
        lastOtp: 'lastOtp',
      };

      const res = await SignUP(payload);
      if (res.message === "success") {
        swal("Success", "Merchant added successfully", "success");
        closeAllModals();
        fetchMerchants();
      }
    } catch (err) {
      swal("Error", err.message || "Failed", "error");
    }
    setLoading(false);
  };

  const handleUpdateMerchant = async () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length) return setErrors(newErrors);

    setLoading(true);
    try {
      const payload = {
        code: editingMerchant.Code,
        name: formData.name,
        mobile: formData.contactNumber,
        company: formData.companyName,
        industry: formData.industry,
        purpose: formData.purpose,
        description: formData.projectDetails,
        status: formData.status ? 1 : 0,
        portalName: "string",
        country: "string",
        timeZone: "string",
        city: "string",
        isRedemption: 0,
        bannerlink: "string",
        logolink: "string",
      };

      if (formData.newPassword) payload.password = formData.newPassword;

      const res = await updateMerchants(payload);
      if (res.message === "success") {
        swal("Success", "Merchant updated successfully", "success");
        closeAllModals();
        fetchMerchants();
      }
    } catch (err) {
      swal("Error", err.message || "Failed", "error");
    }
    setLoading(false);
  };

  const loginAsMerchant = (merchant) => {
    const encoded = btoa(JSON.stringify({ email: merchant.Email, password: merchant.Password }));
    window.open(`/login?data=${encoded}`, "_blank");
  };

  return (
   <DashboardLayout>
      {loading && <Loader />}

      <div className="p-6 max-w-7xl mx-auto">

        {/* HEADER */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Merchant List</h1>
            <p className="text-gray-600">Manage and track all merchants</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition"
          >
            <UserPlusIcon className="w-5 h-5" />
            Add Merchant
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <i className="ri-building-line text-blue-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Merchants</p>
                <p className="text-2xl font-bold text-gray-900">{totalMerchants}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <i className="ri-arrow-up-circle-line text-purple-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Earned Points</p>
                <p className="text-2xl font-bold text-gray-900">{totalEarnedPoints.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <i className="ri-arrow-down-circle-line text-green-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Redeemed</p>
                <p className="text-2xl font-bold text-gray-900">{totalRedeemedValue.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <i className="ri-user-check-line text-orange-600 text-xl"></i>
              </div>
              <div>
                <p className="text-gray-600 text-sm font-medium">Active Merchants</p>
                <p className="text-2xl font-bold text-gray-900">{activeMerchants}</p>
              </div>
            </div>
          </div>
        </div>


        {/* SEARCH + FILTER */}
        <div className="bg-white p-6 rounded-2xl shadow border mb-6 flex flex-col lg:flex-row gap-4 items-center justify-between">
          <input
            type="text"
            placeholder="Search by name, email, code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-5 py-3 border border-gray-300 rounded-xl w-full lg:w-96 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-6 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        {/* TABLE */}
        <div className="bg-white rounded-2xl shadow border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Mobile</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Points</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredMerchants.map((m) => (
                  <tr key={m.Id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 font-medium">{m.Name}</td>
                    <td className="px-6 py-4 text-gray-600">{m.Email}</td>
                    <td className="px-6 py-4">{m.Mobile}</td>
                    <td className="px-6 py-4 font-semibold">{(m.totalPoint || 0).toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${m.Status ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {m.Status ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openDetail(m)} className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs hover:bg-blue-100 transition">
                          <EyeIcon className="w-4 h-4" /> View
                        </button>
                        <button onClick={() => openEditModal(m)} className="flex items-center gap-1 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-xs hover:bg-indigo-100 transition">
                          <PencilIcon className="w-4 h-4" /> Edit
                        </button>
                        <button onClick={() => loginAsMerchant(m)} className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs hover:bg-green-100 transition">
                          <ArrowRightOnRectangleIcon className="w-4 h-4" /> Login
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredMerchants.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-gray-500">
                      No merchants found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showDetailModal && selectedMerchant && (
          <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center p-6 z-50"
            onClick={() => setShowDetailModal(false)}
          >
            <div
              className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-blue-700 text-white px-6 py-4 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold">{selectedMerchant.Name}</h2>
                  <p className="text-sm opacity-90">Code: {selectedMerchant.Code}</p>
                </div>
                <button
                  className="text-3xl hover:opacity-80"
                  onClick={() => setShowDetailModal(false)}
                >
                  ×
                </button>
              </div>

              {/* Content Area */}
              <div className="p-6 overflow-y-auto flex-1">
                {/* Images */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {selectedMerchant.logolink && (
                    <div className="text-center">
                      <p className="font-medium mb-3">Logo</p>
                      <img
                        src={selectedMerchant.logolink}
                        alt="Logo"
                        className="w-32 h-32 object-contain mx-auto rounded-lg border"
                      />
                    </div>
                  )}

                  {selectedMerchant.bannerlink && (
                    <div>
                      <p className="font-medium mb-3">Banner</p>
                      <img
                        src={selectedMerchant.bannerlink}
                        alt="Banner"
                        className="w-full h-48 object-cover rounded-xl border"
                      />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <InfoRow label="Company" value={selectedMerchant.Company} />
                    <InfoRow label="Email" value={selectedMerchant.Email} />
                    <InfoRow label="Mobile" value={selectedMerchant.Mobile} />
                    <InfoRow label="Industry" value={selectedMerchant.Industry} />
                    <InfoRow label="Country" value={selectedMerchant.Country} />
                    <InfoRow label="City" value={selectedMerchant.City} />
                  </div>

                  <div>
                    <InfoRow
                      label="Status"
                      value={selectedMerchant.Status ? "Active" : "Inactive"}
                    />
                    <InfoRow
                      big
                      label="Total Points"
                      value={(selectedMerchant.totalPoint || 0).toLocaleString()}
                    />
                    <InfoRow
                      color="text-green-600"
                      label="Earned Points"
                      value={(selectedMerchant.earnedpoint || 0).toLocaleString()}
                    />
                    <InfoRow
                      color="text-red-600"
                      label="Redeemed"
                      value={(selectedMerchant.RedeemValue || 0).toLocaleString()}
                    />
                    <InfoRow
                      label="Portal Name"
                      value={selectedMerchant.PortalName}
                    />
                    <InfoRow
                      label="Description"
                      value={selectedMerchant.Description}
                    />
                  </div>
                </div>
              </div>

              {/* Footer (Fixed Bottom) */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end items-center">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition shadow"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}


        {(showAddModal || showEditModal) && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50"
            onClick={closeAllModals}
          >
            <div
              className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-blue-700 text-white px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">
                  {showAddModal ? "Add New Merchant" : "Edit Merchant"}
                </h2>

                {/* Close Button */}
                <button
                  onClick={closeAllModals}
                  className="text-3xl leading-none hover:opacity-80"
                >
                  ×
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto flex-1">
                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-medium mb-1">Full Name *</label>
                      <input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                      )}
                    </div>

                    <div>
                      <label className="block font-medium mb-1">Email *</label>
                      <input
                        name="email"
                        type="email"
                        value={formData.email}
                        readOnly={showEditModal}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border rounded-lg ${showEditModal
                            ? "bg-gray-100 cursor-not-allowed"
                            : "focus:ring-2 focus:ring-blue-500"
                          }`}
                      />
                      {errors.email && (
                        <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  {showAddModal && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="relative">
                        <label className="block font-medium mb-1">Password *</label>
                        <input
                          name="newPassword"
                          type={showPassword ? "text" : "password"}
                          value={formData.newPassword}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border rounded-lg pr-12 focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-10 text-gray-500"
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="w-5 h-5" />
                          ) : (
                            <EyeIcon className="w-5 h-5" />
                          )}
                        </button>
                        {errors.newPassword && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.newPassword}
                          </p>
                        )}
                      </div>

                      <div className="relative">
                        <label className="block font-medium mb-1">
                          Confirm Password *
                        </label>
                        <input
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border rounded-lg pr-12 focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          className="absolute right-3 top-10 text-gray-500"
                        >
                          {showConfirmPassword ? (
                            <EyeSlashIcon className="w-5 h-5" />
                          ) : (
                            <EyeIcon className="w-5 h-5" />
                          )}
                        </button>
                        {errors.confirmPassword && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.confirmPassword}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-medium mb-1">
                        Contact Number *
                      </label>
                      <input
                        name="contactNumber"
                        value={formData.contactNumber}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      {errors.contactNumber && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.contactNumber}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block font-medium mb-1">Industry *</label>
                      <select
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Industry</option>
                        {[
                          "Automotive",
                          "E-Commerce",
                          "Technology",
                          "Healthcare",
                          "Finance",
                          "Retail",
                          "Education",
                          "Food & Beverage",
                          "Real Estate",
                          "Other",
                        ].map((i) => (
                          <option key={i} value={i}>
                            {i}
                          </option>
                        ))}
                      </select>
                      {errors.industry && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.industry}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-medium mb-1">
                        Company Name *
                      </label>
                      <input
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      {errors.companyName && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.companyName}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col justify-end">
                      <label className="font-medium mb-2">Status</label>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="status"
                          checked={formData.status}
                          onChange={handleChange}
                          className="sr-only peer"
                        />
                        <div className="w-12 h-6 bg-gray-300 rounded-full peer-checked:bg-green-500 after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:left-[2px] after:top-[2px] peer-checked:after:translate-x-6 after:transition"></div>
                        <span className="ml-3 font-semibold text-gray-700">
                          {formData.status ? "Active" : "Inactive"}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block font-medium mb-1">Purpose *</label>
                    <select
                      name="purpose"
                      value={formData.purpose}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Purpose</option>
                      <option>Launch a new loyalty program</option>
                      <option>Replace existing loyalty program</option>
                      <option>Need more information on loyalty setup</option>
                    </select>
                    {errors.purpose && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.purpose}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block font-medium mb-1">
                      Project Details *
                    </label>
                    <textarea
                      name="projectDetails"
                      value={formData.projectDetails}
                      onChange={handleChange}
                      rows="4"
                      maxLength={500}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    ></textarea>
                    {errors.projectDetails && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.projectDetails}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="tAndC"
                      checked={formData.tAndC}
                      onChange={handleChange}
                      className="w-5 h-5"
                    />
                    <span className="text-sm">
                      I agree to the <b>Terms</b> & <b>Privacy Policy</b>
                    </span>
                  </div>
                  {errors.tAndC && (
                    <p className="text-red-500 text-xs">{errors.tAndC}</p>
                  )}
                </form>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-5 flex gap-4 border-t justify-end">
                <button
                  onClick={closeAllModals}
                  className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-100 transition"
                >
                  Cancel
                </button>

                <button
                  onClick={showAddModal ? handleAddMerchant : handleUpdateMerchant}
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition"
                >
                  {showAddModal ? "Create Merchant" : "Update Merchant"}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}

export default MerchantList;